import React, { useCallback } from 'react'
import { Typography, Grid, Box, Container, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import i18n from 'i18next'
import { useSelector } from 'react-redux'
import { useAuth } from '../context/useFetch'
import { pictureUrl } from '../common/pictureUrl'
import Link from 'next/link'
import { useRouter } from 'next/router'
import moment from 'moment'
import { useUpdate } from '../reducer'

const LIMIT = 10

const useStyles = makeStyles((theme) => ({
  panel: {
    backgroundColor: '#ffffff',
    margin: '20px auto',
    paddingTop: '20px'
  }
}))

/**
 * ActiveCompanies Component
 * 
 * Displays the top 10 most active recruiting companies on the homepage.
 * Companies are sorted by:
 * 1. Number of offers posted in the last month (highest priority)
 * 2. Number of offers posted in the last 2 months
 * 3. Total number of offers (fallback)
 * 
 * This ensures we always show 10 companies even if there's no recent activity.
 * Positioned between job offers and technologies sections as per design.
 * 
 * Behavior:
 * - Clicking a company logo filters job offers by that company (like Technologies section)
 * - Scrolls to the #search anchor and adds Company_${id} filter
 * - Hidden for company/recruiter users
 */
export const ActiveCompanies = () => {
  const classes = useStyles()
  const auth = useAuth()
  const router = useRouter()
  const update = useUpdate()
  const offers = useSelector((state) => state.offers?.data || [])
  const companies = useSelector((state) => state.offers?.included?.companies || {})

  // Don't show for recruiters/companies
  if (auth.isCompany()) {
    return null
  }

  // If no data yet, don't render
  if (offers.length === 0 || Object.keys(companies).length === 0) {
    return null
  }
  
  // Count all offers per company with recent activity prioritized
  const twoMonthsAgo = moment().subtract(2, 'months')
  const oneMonthAgo = moment().subtract(1, 'month')
  
  const companyCounts = {}
  
  offers.forEach(offer => {
    if (offer.status !== 'accepted' || !offer.company) return
    
    const companyId = offer.company.id
    const companyData = companies[companyId]
    
    if (!companyData || !companyData.hashid) return
    
    const offerDate = moment(offer.published_at || offer.created_at)
    const isVeryRecent = offerDate.isAfter(oneMonthAgo)
    const isRecent = offerDate.isAfter(twoMonthsAgo)
    
    if (!companyCounts[companyId]) {
      companyCounts[companyId] = {
        company: companyData,
        veryRecentCount: 0,
        recentCount: 0,
        totalCount: 0
      }
    }
    
    companyCounts[companyId].totalCount++
    if (isRecent) {
      companyCounts[companyId].recentCount++
    }
    if (isVeryRecent) {
      companyCounts[companyId].veryRecentCount++
    }
  })

  // Sort by very recent count first (last month), then recent count (last 2 months), then total count
  // Always show exactly 10 companies
  const topCompanies = Object.values(companyCounts)
    .sort((a, b) => {
      if (b.veryRecentCount !== a.veryRecentCount) {
        return b.veryRecentCount - a.veryRecentCount
      }
      if (b.recentCount !== a.recentCount) {
        return b.recentCount - a.recentCount
      }
      return b.totalCount - a.totalCount
    })
    .slice(0, LIMIT)

  // Don't render if there are no companies
  if (topCompanies.length === 0) {
    return null
  }

  const filterByCompany = useCallback((companyId) => () => {
    location.href = '#search'
    setTimeout(() => {
      update(['filters', 'all'], [`Company_${companyId}`])
    }, 500)
  }, [update])

  return (
    <div className={classes.panel} id="active-companies">
      <Container>
        <Box my={5}>
          <Typography variant="h1" align="center">
            {i18n.t('main.active_companies.title', { defaultValue: 'Entreprises les plus actives' })}
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" style={{ marginBottom: '20px' }}>
            {i18n.t('main.active_companies.subtitle', { defaultValue: 'Découvrez les entreprises qui recrutent le plus ce mois-ci' })}
          </Typography>
          <Grid container justifyContent="center">
            {topCompanies.map(({ company, veryRecentCount, recentCount, totalCount }) => (
              <Grid item xs={6} sm={3} key={company.id}>
                <Box mb={3}>
                  <Typography align="center" variant="h3">
                    {company.name}
                    <br />
                    <IconButton onClick={filterByCompany(company.id)}>
                      <img 
                        style={{ borderRadius: '50%', objectFit: 'contain', width: '70px', height: '70px' }} 
                        src={company.logo ? pictureUrl(company.logo, 70, 70) : '/default-company-logo.png'} 
                        alt={company.name} 
                      />
                    </IconButton>
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </div>
  )
}
